// Keyboard shortcuts: "g" then a letter jumps to a section, "/" focuses search,
// "?" opens the shortcut list. Ignored while typing in a field.

import { useEffect, useRef } from 'react';
import { routeForShortcut, type NavItem } from './nav.js';

export interface ShortcutHandlers {
  navigate: (to: NavItem['to']) => void;
  focusSearch: () => void;
  openHelp: () => void;
  /**
   * The signed-in member's permissions: "g <key>" jumps only to a section they may open
   * (D-070). Undefined while /me loads.
   */
  permissions?: readonly string[] | undefined;
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (target as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(type);
  }
  return target.getAttribute('role') === 'combobox';
}

/** Pure key handler (state: whether "g" was just pressed); returns the new state. */
export function handleShortcutKey(
  event: Pick<KeyboardEvent, 'key' | 'target' | 'ctrlKey' | 'metaKey' | 'altKey'> & {
    preventDefault: () => void;
  },
  awaitingSecond: boolean,
  handlers: ShortcutHandlers,
): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey || isTypingTarget(event.target)) return false;
  if (awaitingSecond) {
    const to = routeForShortcut(event.key, handlers.permissions);
    if (to) {
      event.preventDefault();
      handlers.navigate(to);
    }
    return false;
  }
  if (event.key === 'g') return true;
  if (event.key === '/') {
    event.preventDefault();
    handlers.focusSearch();
  } else if (event.key === '?') {
    event.preventDefault();
    handlers.openHelp();
  }
  return false;
}

export function useShortcuts(handlers: ShortcutHandlers): void {
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  });
  useEffect(() => {
    let awaiting = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onKey = (event: KeyboardEvent) => {
      awaiting = handleShortcutKey(event, awaiting, ref.current);
      clearTimeout(timer);
      if (awaiting) timer = setTimeout(() => (awaiting = false), 1500);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(timer);
    };
  }, []);
}
